import type { AdminEvent, AdminLatest } from "../api.js";

/** Money for the admin tables: cents, a minus sign for money out, and "<$0.01" for a real but tiny cost. */
export function usd(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (value !== 0 && Math.abs(value) < 0.005) return "<$0.01";
  const text = `$${Math.abs(value).toFixed(2)}`;
  return value < 0 ? `−${text}` : text;
}

const short = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const sameDay = (a: Date, b: Date) => a.toISOString().slice(0, 10) === b.toISOString().slice(0, 10);

export function dayLabel(day: Date, now: Date): string {
  const label = short.format(day);
  if (sameDay(day, now)) return `Today · ${label}`;
  if (sameDay(day, new Date(now.getTime() - 86400_000))) return `Yesterday · ${label}`;
  return label;
}

export function groupByDay(events: AdminEvent[], now: Date): { label: string; events: AdminEvent[] }[] {
  const groups: { key: string; label: string; events: AdminEvent[] }[] = [];
  for (const event of events) {
    const at = new Date(event.at);
    const key = at.toISOString().slice(0, 10);
    const last = groups.at(-1);
    if (last?.key === key) last.events.push(event);
    else groups.push({ key, label: dayLabel(at, now), events: [event] });
  }
  return groups.map(({ label, events: list }) => ({ label, events: list }));
}

export type Tone = "ok" | "warn" | "bad" | "muted";

/** A not-found lookup is a warning, not a failure: nothing broke, there was just nothing to find. */
export function latestTone(latest: Pick<AdminLatest, "failed" | "outcome"> | null): Tone {
  if (!latest) return "muted";
  if (latest.outcome === "not_found" && !latest.failed) return "warn";
  return latest.failed ? "bad" : "ok";
}

export const toneClass: Record<Tone, string> = {
  ok: "bg-[color-mix(in_srgb,#15803d_10%,transparent)] text-[#15803d]",
  warn: "bg-[color-mix(in_srgb,#b45309_10%,transparent)] text-[#b45309]",
  bad: "bg-[color-mix(in_srgb,#b91c1c_9%,transparent)] text-[#b91c1c]",
  muted: "bg-bay-800 text-ink-300",
};

/** The time of day in UTC, as the timeline's day headers say. */
export function time(at: string): string {
  return new Date(at).toISOString().slice(11, 16);
}

/** "12 min ago", or "never" for a member who has not been seen since seen-at was recorded. */
export function seen(at: string, now = Date.now()): string {
  const ms = new Date(at).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return "never";
  const minutes = Math.round((now - ms) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
